<!-- カードをグリッド上に表示するコンポーネント -->

<script setup lang="ts">
  import { type ICON_TYPE } from "~/components/Atoms/MenuIcon.vue";

  interface Props {
    showIcon?: boolean | false;
    icon?: ICON_TYPE;
    title?: string;
    showBackButton?: boolean | false;
    showSearch: boolean | false;
    search?: string;
    sortItems?: { text: string; value: string }[];
  }

  interface Emits {
    (event: "clickAll"): void;
    (event: "onBack"): void;
    (event: "update:page", page: number): void;
    (event: "update:search", search: string): void;
  }

  const localProps = withDefaults(defineProps<Props>(), {
    showIcon: false,
    icon: "movie",
    title: "",
    showBackButton: false,
    showSearch: false,
    search: "",
    sortItems: [] as any,
  });

  const emit = defineEmits<Emits>();

  // レスポンシブ対応
  const isSp = isSpDisplayRef();

  const showSearchLayout = ref(false);

  const sort = ref<"desc" | "asc">("desc");
  const sortItem = ref("start_date");
  const searchModel = computed<string>({
    get: () => localProps.search,
    set: value => {
      emit("update:search", value);
    },
  });

  const changeSort = (value: string) => {
    if (sortItem.value === value) {
      sort.value = sort.value === "asc" ? "desc" : "asc";
    } else {
      sortItem.value = value;
      sort.value = "desc";
    }
  };

  const handleShowSearch = () => {
    showSearchLayout.value = !showSearchLayout.value;
    if (!showSearchLayout.value) {
      searchModel.value = "";
    }
  };

  const isTablet = isTabletAndSpDisplayRef();
</script>

<template>
  <div>
    <div class="header_title_wrapper">
      <div class="d-flex align-center">
        <template v-if="!isTablet">
          <div class="d-flex">
            <v-icon
              v-if="showBackButton"
              icon="$mdiChevronLeft"
              :class="{ 'd-none': !isSp }"
              @click="$emit('onBack')"
            />

            <v-sheet v-if="showIcon" class="mr-0 mr-2" color="transparent" :height="isSp ? 22 : 26">
              <AtomsMenuIcon :icon="icon" :width="isSp ? 22 : 26" />
            </v-sheet>

            <span class="header-title">{{ title }}</span>
          </div>
          <div class="flex-grow-1"></div>
        </template>

        <template v-if="isTablet">
          <div v-if="showBackButton" class="back-btn-sp">
            <v-icon icon="$mdiChevronLeft" @click="$emit('onBack')" />
          </div>
          <div class="flex-grow-1">
            <div class="d-flex align-center" :class="{ 'justify-center': showBackButton }">
              <v-sheet v-if="showIcon" class="mr-0 mr-2" color="transparent" :height="isSp ? 22 : 26">
                <AtomsMenuIcon :icon="icon" :width="isSp ? 22 : 26" />
              </v-sheet>
              <span class="header-title">{{ title }}</span>
            </div>
          </div>
        </template>
        <div class="action-btns">
          <slot></slot>

          <v-menu v-if="(sortItems || []).length > 0" scroll-strategy="close">
            <template #activator="{ props }">
              <v-icon
                v-bind="props"
                :icon="sort == 'asc' ? '$mdiSortVariant' : '$mdiSortReverseVariant'"
                :size="isSp ? 'small' : 'default'"
              />
            </template>
            <v-sheet class="bg-monochrome-04">
              <div class="py-2">
                <div
                  v-for="item in sortItems"
                  :key="item.value"
                  class="d-flex justify-space-between cursor-pointer py-2 px-3"
                  @click="changeSort(item.value)"
                >
                  <div class="pr-8">{{ item.text }}</div>
                  <div>{{ item.value === sortItem ? "✔" : "" }}</div>
                </div>
              </div>
            </v-sheet>
          </v-menu>

          <v-icon
            v-if="showSearch"
            icon="$mdiMagnify"
            :size="28"
            class="ml-1"
            :color="showSearchLayout ? 'primary' : 'default'"
            @click="handleShowSearch"
          />
        </div>
      </div>
    </div>
    <div v-if="showSearchLayout" class="popup-search">
      <div class="search-wrapper">
        <v-text-field
          v-model="searchModel"
          class="search-input"
          placeholder="キーワードを入力してください"
          prepend-inner-icon="$mdiMagnify"
          variant="solo"
          autofocus
          clearable
          @keydown.enter.prevent
        ></v-text-field>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";
  .header_title_wrapper {
    padding: 10px 0;
    @include display_tablet {
      padding: 10px 20px;
      border-bottom: 1px solid rgb(var(--v-theme-monochrome-08));
    }
    color: #8a8a8a;
  }
  .header-title {
    color: white;
  }
  .back-btn-sp {
    width: 80px;
  }
  .popup-search {
    padding: 8px 12px;
    background: rgb(var(--v-theme-monochrome-04));
  }
  .search-wrapper {
    padding: 4px 2px;
  }
  .search-input {
    height: 40px;
    width: 100%;
    padding: 0;
  }
  :deep(.v-field--variant-solo) {
    background: rgb(var(--v-theme-monochrome-03));
    border-radius: 20px;
    height: 40px;
    @include display_tablet {
      border-radius: 4px;
    }
  }
  :deep(.v-field__field) {
    margin-top: -10px;
    height: 40px;
  }
  .action-btns {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    @include display_tablet {
      width: 80px;
      gap: 0;
    }
  }
</style>
